/**
 * Boozy Cakes — interactions
 * Mobile nav, FAQ accordion, Splide carousels
 */
(function () {
  "use strict";

  /* ----------------------------------------------------------------------
     Mobile navigation
     ---------------------------------------------------------------------- */
  function initMobileNav() {
    var toggle = document.querySelector(".bzc_nav-toggle");
    var nav = document.getElementById("bzc_nav");
    var overlay = document.getElementById("bzc_nav_overlay");

    if (!toggle || !nav || !overlay) return;

    function setOpen(isOpen) {
      toggle.setAttribute("aria-expanded", String(isOpen));
      toggle.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
      nav.classList.toggle("is-open", isOpen);
      document.body.classList.toggle("bzc_nav-open", isOpen);
      overlay.hidden = !isOpen;
    }

    toggle.addEventListener("click", function () {
      setOpen(!nav.classList.contains("is-open"));
    });

    overlay.addEventListener("click", function () {
      setOpen(false);
    });

    nav.querySelectorAll(".bzc_nav__link").forEach(function (link) {
      link.addEventListener("click", function () {
        setOpen(false);
      });
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") setOpen(false);
    });
  }

  /* ----------------------------------------------------------------------
     FAQ accordion (smooth open / close)
     ---------------------------------------------------------------------- */
  function initFaq() {
    var triggers = document.querySelectorAll(".bzc_accordion__trigger");
    if (!triggers.length) return;

    function getPanel(trigger) {
      var panelId = trigger.getAttribute("aria-controls");
      return panelId ? document.getElementById(panelId) : null;
    }

    function clearInlinePanelStyles(panel) {
      panel.style.height = "";
      panel.style.opacity = "";
      panel.style.overflow = "";
      panel.style.transition = "";
      panel.style.paddingTop = "";
      panel.style.paddingBottom = "";
    }

    function closePanel(trigger, panel, immediate) {
      if (!panel || panel.hidden) {
        trigger.setAttribute("aria-expanded", "false");
        return;
      }

      trigger.setAttribute("aria-expanded", "false");

      if (immediate) {
        panel.hidden = true;
        clearInlinePanelStyles(panel);
        return;
      }

      var startHeight = panel.scrollHeight;
      panel.style.overflow = "hidden";
      panel.style.height = startHeight + "px";
      panel.style.opacity = "1";
      panel.style.transition = "height 0.28s ease, opacity 0.22s ease";

      requestAnimationFrame(function () {
        panel.style.height = "0px";
        panel.style.opacity = "0";
      });

      function onEnd(event) {
        if (event && event.propertyName && event.propertyName !== "height") return;
        panel.removeEventListener("transitionend", onEnd);
        panel.hidden = true;
        clearInlinePanelStyles(panel);
      }

      panel.addEventListener("transitionend", onEnd);
    }

    function openPanel(trigger, panel) {
      if (!panel) return;

      trigger.setAttribute("aria-expanded", "true");
      panel.hidden = false;
      panel.style.overflow = "hidden";
      panel.style.height = "0px";
      panel.style.opacity = "0";
      panel.style.transition = "height 0.28s ease, opacity 0.22s ease";

      requestAnimationFrame(function () {
        panel.style.height = panel.scrollHeight + "px";
        panel.style.opacity = "1";
      });

      function onEnd(event) {
        if (event && event.propertyName && event.propertyName !== "height") return;
        panel.removeEventListener("transitionend", onEnd);
        panel.style.height = "auto";
        panel.style.overflow = "";
        panel.style.transition = "";
        panel.style.opacity = "";
      }

      panel.addEventListener("transitionend", onEnd);
    }

    function closeOthers(activeTrigger) {
      triggers.forEach(function (other) {
        if (other === activeTrigger) return;
        closePanel(other, getPanel(other), false);
      });
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (event) {
        event.preventDefault();

        var panel = getPanel(trigger);
        if (!panel) return;

        var isExpanded = trigger.getAttribute("aria-expanded") === "true";

        if (isExpanded) {
          closePanel(trigger, panel, false);
          return;
        }

        closeOthers(trigger);
        openPanel(trigger, panel);
      });
    });
  }

  /* ----------------------------------------------------------------------
     Make existing Splide pagination dots clickable (no style changes)
     ---------------------------------------------------------------------- */
  function enableClickablePagination(splide) {
    function bindDots() {
      var pages = splide.root.querySelectorAll(".splide__pagination__page");

      pages.forEach(function (btn, index) {
        btn.onclick = function (event) {
          event.preventDefault();
          event.stopPropagation();
          splide.go(">" + index);
        };
      });
    }

    splide.on("mounted", bindDots);
    splide.on("updated", bindDots);
    splide.on("pagination:updated", bindDots);
  }

  /* ----------------------------------------------------------------------
     Splide progress bar helper + click-to-navigate
     ---------------------------------------------------------------------- */
  function bindProgress(splide, root) {
    var track = root.querySelector(".bzc_splide__progress");
    var bar = root.querySelector(".bzc_splide__progress-bar");
    if (!bar || !track) return;

    function getSlideCount() {
      return Math.max(1, splide.length || 1);
    }

    function update() {
      var total = getSlideCount();
      var current = ((splide.index % total) + total) % total;
      var rate = Math.min((current + 1) / total, 1);
      bar.style.width = rate * 100 + "%";
    }

    function goToClickPosition(event) {
      event.preventDefault();
      event.stopPropagation();

      var rect = track.getBoundingClientRect();
      if (!rect.width) return;

      var ratio = (event.clientX - rect.left) / rect.width;
      ratio = Math.max(0, Math.min(1, ratio));

      var total = getSlideCount();
      // Keep last slide reachable when clicking the far right edge
      var slideIndex = ratio >= 1 ? total - 1 : Math.floor(ratio * total);
      slideIndex = Math.max(0, Math.min(total - 1, slideIndex));

      if (splide.Components.Autoplay) {
        splide.Components.Autoplay.pause();
      }

      splide.go(slideIndex);
      update();
    }

    splide.on("mounted", function () {
      track.addEventListener("click", goToClickPosition);
      update();
    });

    splide.on("move resized updated", update);
  }

  /* ----------------------------------------------------------------------
     Carousels
     ---------------------------------------------------------------------- */
  function initProductSlider() {
    var el = document.getElementById("bzc_products_splide");
    if (!el || typeof Splide === "undefined") return;

    var splide = new Splide(el, {
      type: "slide",
      perPage: 4,
      perMove: 1,
      gap: "1.25rem",
      pagination: false,
      arrows: false,
      speed: 600,
      autoplay: true,
      interval: 4500,
      pauseOnHover: true,
      breakpoints: {
        1024: { perPage: 2, gap: "1rem" },
        640: { perPage: 1, gap: "0.85rem" },
      },
    });

    bindProgress(splide, el);
    el._bzcSplide = splide;
    splide.mount();
  }

  function initBestsellerSlider() {
    var el = document.getElementById("bzc_bestsellers_splide");
    if (!el || typeof Splide === "undefined") return;

    var splide = new Splide(el, {
      type: "slide",
      perPage: 4,
      perMove: 1,
      gap: "1.25rem",
      pagination: false,
      arrows: false,
      speed: 600,
      autoplay: true,
      interval: 5000,
      pauseOnHover: true,
      breakpoints: {
        1024: { perPage: 2, gap: "1rem" },
        640: { perPage: 1.25, gap: "0.85rem", focus: "center", trimSpace: false },
      },
    });

    bindProgress(splide, el);
    el._bzcSplide = splide;
    splide.mount();
  }

  function initTestimonialsSlider() {
    var el = document.getElementById("bzc_testimonials_splide");
    if (!el || typeof Splide === "undefined") return;

    var splide = new Splide(el, {
      type: "loop",
      perPage: 3,
      perMove: 1,
      gap: "1.75rem",
      pagination: true,
      arrows: false,
      speed: 650,
      focus: "center",
      trimSpace: false,
      autoplay: false,
      interval: 5500,
      pauseOnHover: true,
      breakpoints: {
        1024: { perPage: 2, gap: "1.25rem", focus: "center" },
        640: { perPage: 1, gap: "1rem", focus: "center" },
      },
    });

    enableClickablePagination(splide);
    splide.mount();
  }
   
  /* ----------------------------------------------------------------------
     Init
     ---------------------------------------------------------------------- */
  document.addEventListener("DOMContentLoaded", function () {
    initMobileNav();
    initFaq();
    initProductSlider();
    initBestsellerSlider();
    initTestimonialsSlider();
  });

  document.addEventListener('click', function (e) {
  const sf_button = e.target.closest(
    '.quick-add[p_id="product_ChooseOptions"] button.quick-add__submit'
  );

  if (!sf_button) return;

  const productUrl = sf_button.getAttribute('data-product-url');

  if (productUrl) {
    window.location.href = productUrl;
  }
});
})();




